import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Switch,
    Checkbox,
} from "@mui/material";
import { ProfileType } from "../../models/profileType";
import { OSMDisplayCard } from "../OSMDisplayCard";
import { ProfileService } from "../../services/profile/profile";
import { useAuthRouting, useSignedInUserState } from "../../hooks/useAuthState";
import { StringsService } from "../../services/strings/strings";
import { useNavigate } from "react-router-dom";
import AsyncString from "../../shared/asyncString";
import "./profileManagement.css";
import { disableGA, initGA } from "../../services/functions/googleAnalytics";
import { viewHightMinusHeader } from "../../header";
import { useInstallPrompt } from "../../contexts/useInstallContext";
import EcoMetricsRow from "./component/Metrics";
import Co2Icon from "@mui/icons-material/Co2";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import PetsIcon from "@mui/icons-material/Pets";
import EcoProfileAvatar from "./component/ProfileAvater";

// helper to fire gtag consent API if available
function updateGtagConsent(granted: boolean) {
    if (granted) { initGA(); } else { disableGA(); }
}

export const ProfileManagementPage = () => {
    useAuthRouting();
    const user = useSignedInUserState().user;
    const navigate = useNavigate();

    const [profile, setProfile] = useState<ProfileType>({
        id: "",
        name: "",
        email: "",
        profileType: "user",
        address: "",
        addressDetails: null,
        paymentDetails: "",
        orders: [],
        createdAt: new Date(),
        isActive: true,
        updatedAt: new Date(),
    });

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [analyticsConsent, setAnalyticsConsent] = useState<boolean>(false);
    const { setShowPrompt } = useInstallPrompt();
    const metrics = [
        {
            label: "CO₂ Saved", value: 0,
            unit: "kg",
            icon: <Co2Icon fontSize="large" sx={{ color: "#4A645F" }} />,
        },
        {
            label: "Water Saved",
            value: 0,
            unit: "L",
            icon: <WaterDropIcon fontSize="large" sx={{ color: "#4A645F" }} />,

        },
        {
            label: "Land Saved",
            value: 0,
            unit: "m²",
            icon: <PetsIcon fontSize="large" sx={{ color: "#4A645F" }} />,
        },
    ];
    // Load profile & consent on mount
    useEffect(() => {
        // 1) fetch profile
        if (user && profile.id === "" && !loading) {
            setLoading(true);
            ProfileService.getProfile(user.uid)
                .then(async (p) => {
                    if (p) {
                        setProfile(p);
                        setErrorMessage("");
                    } else {
                        setErrorMessage(await StringsService.getStringById("error-unknown"));
                    }
                })
                .catch(async () => {
                    setErrorMessage(await StringsService.getStringById("error-unknown"));
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        // 2) read stored consent (from localStorage or cookie)
        console.log(document.cookie)
        const granted = document.cookie.includes("analytics_consent=true");
        setAnalyticsConsent(granted);
        updateGtagConsent(granted);
    }, [user, loading, profile.id]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setProfile({ ...profile, [name]: value });
    };

    // Toggle analytics consent immediately when the switch is flipped
    const handleConsentToggle = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const granted = e.target.checked;
        // write the same cookie that the banner uses
        document.cookie = `analytics_consent=${granted}; path=/; max-age=${60 * 60 * 24 * 365}`;
        setAnalyticsConsent(granted);
        updateGtagConsent(granted);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        setLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            await ProfileService.updateProfile(user.uid, profile);
            setSuccessMessage(await StringsService.getStringById("success-profile-update"));
        } catch {
            setErrorMessage(await StringsService.getStringById("error-profile-update"));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                className="patlite"
                sx={{
                    minHeight: viewHightMinusHeader,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingLeft: 2,
                    paddingRight: 2,
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box
            className="patlite"
            sx={{
                minHeight: viewHightMinusHeader,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: 2,
                paddingRight: 2,
            }}
        >
            <Card
                sx={{
                    width: "100%",
                    maxWidth: 500,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "white",
                }}
            >
                <CardContent>
                    <Typography
                        variant="h5"
                        textAlign="center"
                        marginBottom={2}
                        sx={{ fontWeight: "bold" }}
                    >
                        Profile Management
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
                        <div style={{ padding: 24 }}>
                            <EcoProfileAvatar />
                        </div>

                        <TextField
                            label={<AsyncString stringId={"full-name"} />}
                            name="name"
                            value={profile.name}
                            onChange={handleInputChange}
                            fullWidth
                            margin="normal"
                        />

                        <div style={{ padding: 24 }}>
                            <Typography variant="h6" fontWeight="medium" color="#162C1F" mb={2} textAlign="center">
                                These numbers tell a story — one of care, compassion, and quiet impact
                            </Typography>
                            <EcoMetricsRow metrics={metrics} />
                        </div>

                        {/* Address Details */}
                        <OSMDisplayCard addressDetails={profile.addressDetails} />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={analyticsConsent}
                                    onChange={handleConsentToggle}
                                    sx={{
                                        color: "#162C1F",
                                        '&.Mui-checked': {
                                            color: "#162C1F",
                                        },
                                    }}
                                />
                            }
                            label="Enable Google Analytics"
                            sx={{ marginY: 2, color: "#162C1F" }}
                        />

                        {/* Install As App */}
                        {/* <Button
                            fullWidth
                            sx={{
                                mt: 2,            // shorthand for marginTop: "16px"
                                mb: 2,            // shorthand for marginBottom: "16px"
                                bgcolor: "black", // background-color
                                color: "white",   // text color
                                border: "1px solid black",
                                borderRadius: "24px",
                                "&:hover": {
                                    bgcolor: "#333", // slightly lighter on hover
                                },
                            }}
                            disabled={loading}
                            onClick={() => setShowPrompt(true)}
                        >
                            Install As App
                        </Button> */}

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            variant="outlined"
                            fullWidth
                            sx={{
                                margin: "0px 0px",
                                border: "1px solid #162C1F",
                                borderRadius: "24px",
                            }}
                            disabled={loading}
                        >
                            <Typography sx={{ color: "#162C1F" }} textAlign="center">
                                <AsyncString stringId="update-profile" />
                            </Typography>
                        </Button>
                    </Box>

                    {successMessage && (
                        <Typography color="success.main" marginTop={2} textAlign="center">
                            {successMessage}
                        </Typography>
                    )}
                    {errorMessage && (
                        <Typography color="error.main" marginTop={2} textAlign="center">
                            {errorMessage}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
